/**
 * MedicalInfoStep.constants.js
 *
 * Option data for Screen 6 "Medical Information". id is the stable value
 * persisted to the assessment record and mapped to Rule Engine exclusions
 * downstream (e.g. Diabetes → limit high-glycemic-index foods, Kidney
 * Disease → limit high-potassium/phosphorus foods) — same shape as
 * GoalStep.js's GOAL_OPTIONS / ActivityLevelStep.constants.js's
 * ACTIVITY_TIER_OPTIONS.
 *
 * Catalogue and risk tiers are the closed baseline from FSD v2.1 DEC-09
 * (Medical-condition catalogue) + Section 4 (Medical Rule Catalogue —
 * Launch Policy), mirrored in the AI Nutrition Policy & Rule Engine v1.0
 * Section 7 (Medical Safety Policy). Per that Policy's Section 2 Core
 * Architecture Rule, this file is the single source of truth for the
 * condition → risk-tier mapping; the deterministic Rule/Validation
 * Engine downstream should read CONDITIONS_BY_TIER / the derived id
 * lists below rather than re-deriving tiers itself.
 *
 * CHANGES from the original build:
 *  - Added "Eating Disorder (History or Current Concern)" — DEC-17 /
 *    Section 5 High-Risk Blocking Logic requires this to BLOCK
 *    personalized weight-loss/calorie-deficit plan generation. It was
 *    entirely absent from the original catalogue, which was a safety
 *    gap against the current spec.
 *  - Added Dyslipidaemia, Liver Disease, Gout/Hyperuricaemia, Anaemia,
 *    IBS, Coeliac/Gluten-related disease, and a generic clinician-
 *    restricted-diet catch-all, to match DEC-09's 15-item launch list.
 *  - Renamed high_cholesterol → dyslipidaemia, fatty_liver →
 *    liver_disease, gastric_acidity → gerd_reflux to match the approved
 *    catalogue's naming so this list, the FSD table, and any backend
 *    rule IDs stay in sync.
 *  - REMOVED "food_allergies" and "heart_disease". Food allergies are
 *    not in the DEC-09 catalogue — Nutrition Policy Section 8 requires
 *    allergies to be captured as a separate field with absolute
 *    ingredient-level exclusion and severity, which a same-tier
 *    checkbox here can't represent. Heart disease isn't part of the
 *    approved 15-condition launch list and has no approved rule set to
 *    map to, so surfacing it here would let a condition reach the plan
 *    generator with no deterministic rule behind it. If Product wants
 *    a dedicated allergy field on this screen, that should be modeled
 *    separately per Section 10 (Allergy & Food Safety Data Model), not
 *    folded back into this list.
 *
 * Pregnancy/GDM and Breastfeeding are intentionally NOT included here —
 * per FSD v1.0 Screen 11 (Women's Health) and the Pregnancy/Breastfeeding
 * engines (Nutrition Policy Sections 5–6), those use a dedicated pathway,
 * not this generic medical-conditions list.
 */

export const NONE_CONDITION_ID = "none";

// Deterministic plan behavior per condition (FSD v2.1 §4 "Default plan
// behavior" column). The Rule/Validation Engine — not the AI — decides
// block/warn from these tiers (§5: "AI never decides whether a user is
// high-risk").
export const RISK_TIER = {
  // Generate a normal plan; condition is informational / low-impact.
  GENERATE: "generate",
  // Generate, but apply condition-specific restriction rules.
  CAUTION: "caution",
  // Generate only within an approved restricted rule set, or route to a
  // professional-review notice — this screen cannot tell which without
  // backend clinical parameters, so both selection states surface the
  // same professional-review notice here.
  PROFESSIONAL_REVIEW: "professional_review",
  // Do not generate a personalized calorie-deficit/weight-loss plan.
  // Show a supportive, non-diagnostic message and route to a safe
  // next step instead (§5, §18 "High risk" disclaimer).
  BLOCK: "block",
};

export const CONDITION_OPTIONS = [
  { id: "diabetes", label: "Diabetes", riskTier: RISK_TIER.CAUTION },
  { id: "hypertension", label: "Hypertension", riskTier: RISK_TIER.CAUTION },
  { id: "dyslipidaemia", label: "Dyslipidaemia (High Cholesterol)", riskTier: RISK_TIER.GENERATE },
  { id: "kidney_disease", label: "Kidney Disease", riskTier: RISK_TIER.PROFESSIONAL_REVIEW },
  { id: "liver_disease", label: "Liver Disease", riskTier: RISK_TIER.CAUTION },
  { id: "thyroid_disorder", label: "Thyroid Disorder", riskTier: RISK_TIER.CAUTION },
  { id: "pcos_pcod", label: "PCOS / PCOD", riskTier: RISK_TIER.GENERATE },
  { id: "gout_hyperuricaemia", label: "Gout / Hyperuricaemia", riskTier: RISK_TIER.CAUTION },
  { id: "anaemia", label: "Anaemia", riskTier: RISK_TIER.GENERATE },
  { id: "gerd_reflux", label: "GERD / Acid Reflux", riskTier: RISK_TIER.GENERATE },
  { id: "ibs", label: "IBS (Irritable Bowel Syndrome)", riskTier: RISK_TIER.CAUTION },
  {
    id: "coeliac_gluten",
    label: "Coeliac Disease / Gluten Sensitivity",
    riskTier: RISK_TIER.GENERATE,
    // Ingredient-level gluten exclusion, never relaxed to a soft
    // preference — DEC-09: "Hard gluten exclusion if diagnosed/selected".
    hardExclusion: true,
  },
  {
    id: "clinician_restricted_diet",
    label: "Other Doctor-Prescribed Diet Restriction",
    riskTier: RISK_TIER.PROFESSIONAL_REVIEW,
  },
  {
    id: "eating_disorder_concern",
    label: "Eating Disorder (History or Current Concern)",
    riskTier: RISK_TIER.BLOCK,
  },
  { id: NONE_CONDITION_ID, label: "None", riskTier: null },
];

// Rule-registry metadata (Nutrition Policy & Rule Engine v1.0, Section 21
// "Rule Registry & Versioning" — MED-* id pattern).
export const MEDICAL_CATALOGUE_RULE = {
  ruleId: "MED-CATALOGUE-001",
  source: "FSD v2.1 DEC-09/§4 / Nutrition Policy & Rule Engine v1.0 §7",
  version: "2.1",
};

// Derived id lists, grouped by tier — kept in sync with CONDITION_OPTIONS
// automatically so nothing can drift between the display list and the
// logic that reads it.
const conditionIdsByTier = CONDITION_OPTIONS.reduce((acc, condition) => {
  if (condition.id === NONE_CONDITION_ID) return acc;
  if (!acc[condition.riskTier]) acc[condition.riskTier] = [];
  acc[condition.riskTier].push(condition.id);
  return acc;
}, {});

export const CAUTION_CONDITION_IDS = conditionIdsByTier[RISK_TIER.CAUTION] || [];
export const PROFESSIONAL_REVIEW_CONDITION_IDS =
  conditionIdsByTier[RISK_TIER.PROFESSIONAL_REVIEW] || [];
// BLOCKING_CONDITION_IDS: selecting any of these must prevent a
// personalized weight-loss/calorie-deficit plan at generation time
// (§5). Currently just eating-disorder concern, kept as a list (not a
// single constant) since DEC-17's blocking criteria may grow.
export const BLOCKING_CONDITION_IDS = conditionIdsByTier[RISK_TIER.BLOCK] || [];

export const MEDICATIONS_MAX_LENGTH = 200;

export const MEDICAL_INITIAL_DATA = {
  conditions: [],
  medications: "",
};